import { useEffect, useRef, useState } from "react";
import { bool, func, string } from "prop-types";
import { useHistory, useLocation } from "react-router-dom";

import { routes } from "../../constants";
import { validateInput } from "../../helpers";

import {
  AuthFormActions,
  AuthFormButton,
  AuthFormHeader,
} from "../../styles/components/AuthForm";
import Form from "../form";
import Group from "../form/components/Group";
import Label from "../form/components/Label";
import Modal from "../modal";
import Text from "../form/components/Text";

const ModalReauthorize = ({
  login,
  showModalReauthorize,
  toggleModalReauthorize,
  user,
}) => {
  const [data, setData] = useState({ email: "" });
  const [errors, setErrors] = useState({ email: null, password: null });
  const [inProgress, setInProgress] = useState(false);

  const form = useRef();
  let history = useHistory();
  let location = useLocation();

  /**
   * Handle API error
   * @param {*} error
   * @param {string} email
   * @public
   */
  const handleApiError = (error, email) => {
    setInProgress(false);
    toggleModalReauthorize();
    history.push({
      pathname: routes.login.path,
      state: {
        errorMessage:
          "The email and password combination you entered did not match, and your session has been reset for security purposes.",
        from: location.pathname,
        email: email,
      },
    });
  };

  /**
   * Handle form input
   * @param {event} event
   * @public
   */
  const handleInput = event => {
    setData({ ...data, [event.target.name]: event.target.value });
  };

  /**
   * Handle form submission
   * @param {event} event
   * @public
   */
  const handleSubmit = event => {
    event.preventDefault();

    const data = {};
    let hasError = false;

    if (!!form.current) {
      [...form.current.elements].forEach(input => {
        if (!!input.id) {
          const error = validateInput(input) || null;
          hasError = !!error ? true : hasError;
          setErrors(prevErrors => ({ ...prevErrors, [input.id]: error }));
          data[input.id] = !!input ? input.value : null;
        }
      });
    }

    if (!hasError) {
      postData(data);
    }
  };

  /**
   * Post data to API
   * @param {object} data
   * @public
   */
  const postData = data => {
    setInProgress(true);

    login(data)
      .then(response => {
        if (response.success) {
          toggleModalReauthorize();
        } else {
          handleApiError(response, data.email);
        }
      })
      .catch(error => handleApiError(error, data.email));
  };

  // Set initial value for email field
  useEffect(() => {
    if (!!user) {
      setData(prevData => ({
        ...prevData,
        email: !prevData.email ? user.email : prevData.email,
      }));
    }
  }, [user]);

  return (
    <Modal
      aria-label="Reauthorize Login"
      open={showModalReauthorize}
      size="small"
      zIndex={99999999999}
    >
      <AuthFormHeader>
        <h1>Your session has expired.</h1>
        <p>Please log-in again below to resume.</p>
      </AuthFormHeader>
      <Form onSubmit={handleSubmit} formRef={form}>
        <div className="form-fields">
          <Group error={errors.email}>
            <Label htmlFor="email" required>
              Email
            </Label>
            <Text
              id="email"
              maxLength="200"
              name="email"
              onChange={handleInput}
              required
              type="email"
              value={data.email}
            />
          </Group>
          <Group error={errors.password} labelOffset="smaller">
            <Label htmlFor="password" required>
              Password
            </Label>
            <Text id="password" name="password" required type="password" />
          </Group>
        </div>
        <AuthFormActions>
          <AuthFormButton disabled={inProgress}>
            {inProgress ? "Logging in…" : "Login"}
          </AuthFormButton>
        </AuthFormActions>
      </Form>
    </Modal>
  );
};

ModalReauthorize.propTypes = {
  login: func.isRequired,
  showModalReauthorize: bool,
  targetDevice: string,
  toggleModalReauthorize: func.isRequired,
};

export default ModalReauthorize;
